'use client';

import { Dialog, Flex, Text, ButtonGroup, Button } from '@/_components';
import { useContext, useCallback, useMemo, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { ClientDownloadMultipleDocumentsDialogProps } from './ClientDownloadMultipleDocumentsDialog.types';
import { downloadMultipleDocumentsFromBrowser } from '@/_api/client-documents';

export const ClientDownloadMultipleDocumentsDialog: FC<
  ClientDownloadMultipleDocumentsDialogProps
> = ({ isOpen, onClose, documents, companyId, onDownloaded }) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const handleDownloadMultiple = useCallback(async () => {
    try {
      downloadMultipleDocumentsFromBrowser({
        companyId,
        documents,
        token,
      });

      onDownloaded();
    } catch (e) {
      console.error(e);
      addToast('error', 'There was a problem downloading the folder.');
    } finally {
      onClose();
    }
  }, [companyId, documents, addToast, onClose, token, onDownloaded]);

  const files = useMemo(() => {
    return documents.filter((doc) => doc.type === 'file');
  }, [documents]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <Dialog.Content>
        <Flex isColumn gap={6}>
          {files.length === 0 ? (
            <>
              <Text>
                Downloading folders is not supported. Please select files only.
              </Text>

              <ButtonGroup align="right">
                <Button intent="secondary" onClick={onClose} value="Okay" />
              </ButtonGroup>
            </>
          ) : (
            <>
              <Text>
                You are about to download {files.length} files. (Note: Folders
                cannot be downloaded. If multiple files do not download, please
                enable pop-ups in your browser settings.)
              </Text>

              <ButtonGroup align="right">
                <Button intent="secondary" onClick={onClose} value="Cancel" />
                <Button
                  intent="primary"
                  onClick={handleDownloadMultiple}
                  value={`Download ${files.length} files`}
                />
              </ButtonGroup>
            </>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
